/*
 * The patient's default pharmacy, shared by the screens that ask for it.
 *
 * GET /v1/{pid}/rx/patient/{patient}/department/{dept}/pharmacy is a billed Athena call
 * (chart/{patient}/pharmacies/default). It used to run on every Vitals tab mount,
 * every print and every Refills / New Rx open: about 2.7 calls per patient-day
 * (Athena call plan item 9, 10-15). Every screen asks this module, in one of two ways:
 *
 *  - cache: true, ONLY from the read-only screens that SHOW the pharmacy (Vitals intake,
 *    Print Notes, the Refills panel since 10-20). Such a read may use an answer this
 *    browser already has (kept TTL_MS, 1 h; a failure, e.g. no default pharmacy:
 *    Athena 404 -> v2 500, FAILURE_TTL_MS, 10 min), joins any read already in flight,
 *    and otherwise asks with ?cache=1, which lets v2 answer from its own per-patient
 *    cache (server contract: v2 serves a cached pharmacy only to a request that sends
 *    cache=1; without it v2 asks Athena). An old v2 or v1 ignores the parameter and
 *    asks Athena, as before.
 *  - fresh (the default; never ?cache=1): the moments the pharmacy is USED (New Rx open,
 *    Refills > Add medication, the read after our own Change pharmacy). They write the
 *    pharmacy into a prescription, so they never use a kept answer, here or on the
 *    server. A fresh read joins only another fresh read still in flight (it is as
 *    fresh), never a cache=1 one; its answer replaces the kept one, so the showing
 *    screens see the newest pharmacy.
 *
 * forgetDefaultPharmacy drops the patient's kept answers after our own change.
 * Resolves to the api client's body ({status, data, message}), exactly what the screens
 * got before; rejects with the client's error. `client` is the axios client to ask with
 * (default: the app's api.js; Print Notes passes its own v2 client): both gateways answer
 * this route with the same body, so one kept answer serves every screen.
 */
import api from "./api";

export const DEFAULT_PHARMACY_TTL_MS = 60 * 60 * 1000;
export const DEFAULT_PHARMACY_FAILURE_TTL_MS = 10 * 60 * 1000;

const entries = new Map(); // key -> {at, pending, fresh, failed, error, promise}

const keyOf = (practiceId, patientId, departmentId) => `${practiceId}|${patientId}|${departmentId}`;

// The department to ask for: the patient's (the appointment / chart row's departmentid),
// else `fallback` (the user's department), else 1. Was the user's department or a
// hard-coded 1, so a Frisco (dept 2) patient was asked under Odessa.
export function pharmacyDepartmentId(row, fallback) {
  const pick = (v) => {
    const n = Number(v);
    return Number.isInteger(n) && n > 0 ? n : null;
  };
  return pick(row?.departmentid) || pick(fallback) || 1;
}

export function defaultPharmacyUrl(practiceId, patientId, departmentId, { cache = false } = {}) {
  const url = `/v1/${practiceId}/rx/patient/${patientId}/department/${departmentId}/pharmacy`;
  return cache ? `${url}?cache=1` : url;
}

const isKept = (hit) => {
  if (!hit) return false;
  if (hit.pending) return true;
  const age = Date.now() - hit.at;
  return hit.failed ? age < DEFAULT_PHARMACY_FAILURE_TTL_MS : age < DEFAULT_PHARMACY_TTL_MS;
};

export function getDefaultPharmacy({ practiceId, patientId, departmentId, cache = false, client = api } = {}) {
  const key = keyOf(practiceId, patientId, departmentId);
  const hit = entries.get(key);
  const usable = cache ? isKept(hit) : !!hit && hit.pending && hit.fresh;
  if (usable) {
    return hit.failed ? Promise.reject(hit.error) : hit.promise;
  }
  const entry = { at: Date.now(), pending: true, fresh: !cache, failed: false, error: null, promise: null };
  entry.promise = client.get(defaultPharmacyUrl(practiceId, patientId, departmentId, { cache })).then(
    (body) => {
      entry.pending = false;
      entry.at = Date.now();
      return body;
    },
    (error) => {
      entry.pending = false;
      entry.failed = true;
      entry.error = error;
      entry.at = Date.now();
      throw error;
    },
  );
  entries.set(key, entry);
  return entry.promise;
}

// Drop every kept answer for this patient (all departments): after our own change.
export function forgetDefaultPharmacy(practiceId, patientId) {
  const prefix = `${practiceId}|${patientId}|`;
  Array.from(entries.keys()).forEach((k) => {
    if (k.startsWith(prefix)) entries.delete(k);
  });
}

// Tests only.
export function resetDefaultPharmacyCache() {
  entries.clear();
}
